import { type Ref, ref, watch } from 'vue'
import {defineStore, storeToRefs} from 'pinia'
import type { Cart} from '@hubblecommerce/hubble/platforms/shopware/api-client';
import {CartShopware, OpenAPI} from '@hubblecommerce/hubble/platforms/shopware/api-client'
import { useNotification, usePlatform, hblMapCart, hblCartIncludes, hblMapMiniCart, useCookie, useRuntimeConfig } from '#imports'
import type { HblCart, HblIUseCart, HblMiniCart, HblLineItem, MiniCartItem } from '@/utils/types'
import {request as __request} from "@hubblecommerce/hubble/platforms/shopware/request";

export const useCart = defineStore('use-cart', (): HblIUseCart => {
    const cart: Ref<HblCart | null> = ref(null)
    const miniCart: Ref<HblMiniCart | null> = ref(null)

    const { cartCookie } = useRuntimeConfig().public as { cartCookie: { name: string, options: any } }
    const error: Ref = ref(false)
    const loading: Ref<boolean> = ref(false)
    const { setSessionToken } = usePlatform()
    const { showNotification } = useNotification()

    async function getCart (): Promise<HblCart | void> {
        loading.value = true
        error.value = false

        try {
            // @ts-ignore
            const response = await CartShopware.readCart({
                includes: hblCartIncludes
            })

            if (response.token !== undefined) {
                setSessionToken(response.token)
            }

            const mappedData = hblMapCart(response)
            cart.value = mappedData
            loading.value = false

            return mappedData
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function deleteCart (): Promise<void> {
        error.value = false
        loading.value = true

        try {
            await CartShopware.deleteCart()
            cart.value = null
            loading.value = false
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function removeLineItem (id: string): Promise<HblCart | void> {
        error.value = false
        loading.value = true

        try {
            const items = []
            items.push(id)

            const response = await CartShopware.removeLineItem({ ids: items })

            if (response.token !== undefined) {
                setSessionToken(response.token)
            }

            const mappedData = hblMapCart(response)
            cart.value = mappedData
            loading.value = false

            return mappedData
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function updateLineItem (lineItem: HblLineItem | MiniCartItem, updatedQty: number): Promise<Cart> {
        return CartShopware.updateLineItem(
            {
                includes: hblCartIncludes,
                items: [
                    // @ts-ignore
                    {
                        id: lineItem.id,
                        quantity: updatedQty
                    }
                ]
            }
        )
    }

    function addLineItem (itemId: string, qty: number, additionalPayload?: Record<string, any>) {
        const setProductStore = useSetProduct()
        // @ts-ignore
        const { configuredSetPayload } = storeToRefs(setProductStore)

        // Build payload object
        const payload: Record<string, any> = {}

        // Add set product payload if exists
        if (configuredSetPayload.value) {
            payload.productSet = configuredSetPayload.value
        }

        // Add additional payload (e.g., abo data) if exists
        if (additionalPayload) {
            Object.assign(payload, additionalPayload)
        }

        return CartShopware.addLineItem(
            {
                items: [
                    {
                        type: 'product',
                        id: itemId,
                        quantity: qty,
                        ...(Object.keys(payload).length > 0 && { payload }),
                    }
                ]
            }
        )
    }

    async function addToCart (qty: number, itemId: string, additionalPayload?: Record<string, any>): Promise<HblCart | void> {
        error.value = false
        loading.value = true

        try {
            // @ts-ignore
            const response = await addLineItem(itemId, qty, additionalPayload)

            if (response.token !== undefined) {
                setSessionToken(response.token)
            }

            const mappedData = hblMapCart(response)
            cart.value = mappedData
            showNotification('addToCart', 'success')
            loading.value = false

            return mappedData
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    async function addGreetingCardToCart (productId: string, greetingCardId: string, greetingCardText: string): Promise<HblCart | void> {
        error.value = false
        loading.value = true

        try {
            const response: any = await __request(OpenAPI, {
                method: 'POST',
                url: '/greeting-card/add-to-cart',
                body: {
                    "greetingCard": greetingCardId,
                    "greetingCardProductId": productId,
                    "greetingCardText": greetingCardText,
                    "greetingCardQuantity": 1
                }
            });

            const mappedData = hblMapCart(response.cart)
            cart.value = mappedData
            loading.value = false

            return mappedData
        } catch (e) {
            loading.value = false
            error.value = e
        }
    }

    function saveCart (): void {
        miniCart.value = cart.value != null ? hblMapMiniCart(cart.value) : null

        const cookie = useCookie(cartCookie.name, cartCookie.options)
        // @ts-ignore
        cookie.value = miniCart.value
    }

    async function addCoupon (code: string): Promise<string | void> {
        error.value = false
        loading.value = true

        try {
            code = code.trim()

            if (code === '') {
                loading.value = false
                return
            }

            const response = await CartShopware.addLineItem(
                {
                    includes: hblCartIncludes,
                    items: [
                        // @ts-ignore
                        {
                            type: 'promotion',
                            referencedId: code
                        }
                    ]
                }
            )

            let successfullyAddedCoupon: boolean = true
            let message: string = ''
            if (response.errors != null) {
                for (const [key, error] of Object.entries(response.errors)) {
                    if (key.includes('promotion')) {
                        if (key.includes('promotion-discount-added')) {
                            message = error.message as string
                            break
                        } else if ((key.includes('promotion-not-eligible') && error.parameters?.name?.includes(code) || key.includes('promotion-not-found') && error.parameters?.code?.includes(code))) {
                            message = error.message as string
                            successfullyAddedCoupon = false
                            break
                        }
                    } else {
                        message = error.message as string
                        successfullyAddedCoupon = false
                    }
                }
            }

            if(!successfullyAddedCoupon) {
                throw new Error(message)
            }

            if (response.token !== undefined) {
                setSessionToken(response.token)
            }

            if (response.lineItems?.length === 0) {
                loading.value = false
                return
            }

            const mappedData = hblMapCart(response)
            cart.value = mappedData
            loading.value = false

            return message
        } catch (e) {
            loading.value = false
            throw e
        }
    }

    watch(cart, (value, oldValue, onCleanup) => {
        saveCart()
    })

    return {
        cart,
        miniCart,
        getCart,
        deleteCart,
        addToCart,
        addGreetingCardToCart,
        removeLineItem,
        updateLineItem,
        // @ts-ignore
        addCoupon,
        loading,
        error,
    }
})
